const LS_PREFIX = "ff_override:";
const URL_PARAM = "ff";

/**
 * Check localStorage for a flag override.
 * Returns true/false if overridden, undefined if not.
 */
export function getLocalOverride(flagKey: string): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  const val = localStorage.getItem(`${LS_PREFIX}${flagKey}`);
  if (val === "true") return true;
  if (val === "false") return false;
  return undefined;
}

/**
 * Set a localStorage override for a flag.
 */
export function setLocalOverride(flagKey: string, value: boolean): void {
  localStorage.setItem(`${LS_PREFIX}${flagKey}`, String(value));
}

/**
 * Clear a localStorage override.
 */
export function clearLocalOverride(flagKey: string): void {
  localStorage.removeItem(`${LS_PREFIX}${flagKey}`);
}

/**
 * Clear all flag overrides from localStorage.
 */
export function clearAllOverrides(): void {
  if (typeof window === "undefined") return;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(LS_PREFIX)) keys.push(key);
  }
  keys.forEach((k) => localStorage.removeItem(k));
}

/**
 * Parse URL search params for flag overrides.
 * Format: ?ff=delivery.customer.live_map:true,delivery.portal.login:false
 */
export function getUrlOverrides(): Map<string, boolean> {
  const overrides = new Map<string, boolean>();
  if (typeof window === "undefined") return overrides;
  const params = new URLSearchParams(window.location.search);
  const ffParam = params.get(URL_PARAM);
  if (!ffParam) return overrides;

  for (const pair of ffParam.split(",")) {
    const [key, val] = pair.split(":");
    if (key && (val === "true" || val === "false")) {
      overrides.set(key.trim(), val === "true");
    }
  }
  return overrides;
}

/**
 * Install window.__FF for console access in development.
 */
export function installConsoleBridge(): void {
  if (typeof window === "undefined") return;
  if (import.meta.env.PROD) return;

  (window as unknown as Record<string, unknown>).__FF = {
    enable: (key: string) => {
      setLocalOverride(key, true);
      console.log(`[FF] ${key} → enabled (reload to apply)`);
    },
    disable: (key: string) => {
      setLocalOverride(key, false);
      console.log(`[FF] ${key} → disabled (reload to apply)`);
    },
    clear: (key: string) => {
      clearLocalOverride(key);
      console.log(`[FF] ${key} → override cleared (reload to apply)`);
    },
    clearAll: () => {
      clearAllOverrides();
      console.log("[FF] All overrides cleared (reload to apply)");
    },
    list: () => {
      const overrides: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(LS_PREFIX)) {
          overrides[key.replace(LS_PREFIX, "")] = localStorage.getItem(key) ?? "";
        }
      }
      console.table(overrides);
    },
  };
}
